import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../context/ToastContext';
import {
  Box, Typography, Paper, Tabs, Tab, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Stack, Chip, IconButton, Card, CardContent,
  Alert, CircularProgress, Grid, Tooltip,
} from '@mui/material';
import {
  IconPlus, IconEdit, IconTrash, IconShoppingCart, IconCash,
  IconTrendingUp, IconTrendingDown, IconBox, IconMinus,
  IconTools, IconPrinter, IconTruck, IconUsers,
  IconPhone, IconMail, IconMapPin, IconCategory,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import {
  fetchStorePurchasesData,
  createPurchase,
  deletePurchase,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  recordInventoryUsage,
  adjustInventoryStock,
  addProduction,
} from '../slices/storePurchasesSlice';
import { fetchItemCategories, createItemCategory } from '../slices/storeSlice';
import { fetchAuthorizedPayers } from '../slices/authorizedPayersSlice';

const MotionPaper = motion(Paper);
const FONT = 'Cairo, Tajawal, sans-serif';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const emptyForm = (date) => ({
  category: '',
  quantity: '',
  unit_price: '',
  date: date || new Date().toISOString().split('T')[0],
});

const TransactionDialog = ({ open, onClose, title, form, setForm, onSave, saving, itemCategories, color='primary', suppliers }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT, direction: 'rtl' } }}
  >
    <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, color: '#4338CA', fontSize: '1.15rem' }}>
      {title}
    </DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {suppliers && (
          <FormControl fullWidth required>
            <InputLabel>المورد</InputLabel>
            <Select value={form.authorized_payer || ''} label="المورد" onChange={e => setForm({ ...form, authorized_payer: e.target.value })}>
              <MenuItem value="">— بدون مورد —</MenuItem>
              {(suppliers || []).map(s => <MenuItem key={s.id} value={s.id}>{s.name || s.display_value}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth required>
          <InputLabel>النوع</InputLabel>
          <Select value={form.category} label="النوع" onChange={e => setForm({ ...form, category: e.target.value })}>
            {(itemCategories || []).length === 0
              ? <MenuItem disabled value="">— أضف أنواعاً من إدارة المستودع أولاً —</MenuItem>
              : (itemCategories || []).map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)
            }
          </Select>
        </FormControl>
        <TextField fullWidth required label="الكمية" type="number" inputProps={{ min: 1 }}
          value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
        <TextField fullWidth required label="السعر (₪)" type="number" inputProps={{ min: 0, step: 0.01 }}
          value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} />
        {form.quantity && form.unit_price && (
          <Alert severity="info" sx={{ borderRadius: 2, fontFamily: FONT }}>
            الإجمالي: {(parseFloat(form.quantity||0) * parseFloat(form.unit_price||0)).toFixed(2)} ₪
          </Alert>
        )}
        <TextField fullWidth required label="التاريخ" type="date" InputLabelProps={{ shrink: true }}
          value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}>
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, color: '#6B7280', borderColor: '#E5E7EB' }}
      >
        إلغاء
      </Button>
      <Button
        variant="contained"
        color={color}
        onClick={onSave}
        disabled={saving}
        sx={{
          borderRadius: 2.5,
          textTransform: 'none',
          fontWeight: 700,
          fontFamily: FONT,
          bgcolor: '#4338CA',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
        }}
      >
        {saving ? 'جاري الحفظ...' : 'حفظ'}
      </Button>
    </DialogActions>
  </Dialog>
);

const StorePurchasesPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState(0);

  // date filters – sales tab
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // date filters – purchases tab
  const [spDateFilter, setSpDateFilter] = useState('all');
  const [spDateFrom, setSpDateFrom] = useState('');
  const [spDateTo, setSpDateTo] = useState('');

  const {
    suppliers, purchases, supplierPurchases, inventory, summary, loading, saving,
  } = useSelector((s) => s.storePurchases || {});

  const { itemCategories } = useSelector((s) => s.store || {});
  const { items: authorizedPayers = [] } = useSelector((s) => s.authorizedPayers || {});

  // ── dialogs ────────────────────────────────────────────────────────────────
  const [saleOpen, setSaleOpen] = useState(false);
  const [saleForm, setSaleForm] = useState(emptyForm());

  const [buyOpen, setBuyOpen] = useState(false);
  const [buyForm, setBuyForm] = useState({ ...emptyForm(), authorized_payer: '' });

  // supplier CRUD
  const [supOpen, setSupOpen] = useState(false);
  const [editSup, setEditSup] = useState(null);
  const [supForm, setSupForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });

  // inventory
  const [useOpen, setUseOpen] = useState(false);
  const [selInvItem, setSelInvItem] = useState(null);
  const [useData, setUseData] = useState({ quantity: '', notes: '' });

  const [prodOpen, setProdOpen] = useState(false);
  const [prodData, setProdData] = useState({ item_name: '', quantity: '', purchase_price: '', sale_price: '', notes: '' });

  // add category dialog
  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', description: '', quantity: '' });
  const [catSaving, setCatSaving] = useState(false);

  // add stock dialog
  const [addOpen, setAddOpen] = useState(false);
  const [selAddItem, setSelAddItem] = useState(null);
  const [addQty, setAddQty] = useState('');

  useEffect(() => {
    dispatch(fetchStorePurchasesData());
    dispatch(fetchItemCategories());
    dispatch(fetchAuthorizedPayers());
  }, [dispatch]);

  // ── date filtering ─────────────────────────────────────────────────────────
  const filterByDate = (rows, dateField, filter, from, to) => {
    if (filter === 'all') return rows;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (filter === 'today') return rows.filter(r => {
      const d = new Date(r[dateField]); d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    });
    if (filter === 'week') {
      const w = new Date(today); w.setDate(w.getDate() - 7);
      return rows.filter(r => { const d = new Date(r[dateField]); d.setHours(0,0,0,0); return d >= w && d <= today; });
    }
    if (filter === 'custom') {
      let res = rows;
      if (from) res = res.filter(r => new Date(r[dateField]) >= new Date(from));
      if (to)   res = res.filter(r => new Date(r[dateField]) <= new Date(to + 'T23:59:59'));
      return res;
    }
    return rows;
  };

  const filteredSales = filterByDate(purchases || [], 'purchase_date', dateFilter, dateFrom, dateTo);

  const filteredBuys  = filterByDate(supplierPurchases || [], 'purchase_date', spDateFilter, spDateFrom, spDateTo);

  const fmtDate = (s) => {
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  };

  const total = (rows) => rows.reduce((s, r) => s + Number(r.total_amount || r.quantity * r.unit_price || 0), 0);

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleSaveSale = async () => {
    if (!saleForm.category) { showToast('❌ يرجى اختيار النوع'); return; }
    if (!saleForm.quantity || parseInt(saleForm.quantity) <= 0) { showToast('❌ يرجى إدخال الكمية'); return; }
    if (!saleForm.unit_price || parseFloat(saleForm.unit_price) <= 0) { showToast('❌ يرجى إدخال السعر'); return; }
    const existing = (inventory || []).find(i => i.item_name === saleForm.category);
    if (existing && existing.current_stock < parseInt(saleForm.quantity)) {
      showToast(`❌ الكمية المتاحة: ${existing.current_stock}`); return;
    }
    try {
      await dispatch(createPurchase({
        item_name: saleForm.category,
        category: saleForm.category,
        quantity: parseInt(saleForm.quantity),
        unit_price: parseFloat(saleForm.unit_price),
        purchase_date: saleForm.date,
      })).unwrap();
      showToast('✅ تم تسجيل البيع');
      setSaleOpen(false); setSaleForm(emptyForm());
      dispatch(fetchStorePurchasesData());
    } catch { showToast('❌ فشل في تسجيل البيع'); }
  };

  const handleSaveBuy = async () => {
    if (!buyForm.category) { showToast('❌ يرجى اختيار النوع'); return; }
    if (!buyForm.quantity || parseInt(buyForm.quantity) <= 0) { showToast('❌ يرجى إدخال الكمية'); return; }
    if (!buyForm.unit_price || parseFloat(buyForm.unit_price) <= 0) { showToast('❌ يرجى إدخال السعر'); return; }
    try {
      await dispatch(createPurchase({
        item_name: buyForm.category,
        category: buyForm.category,
        quantity: parseInt(buyForm.quantity),
        unit_price: parseFloat(buyForm.unit_price),
        purchase_date: buyForm.date,
        authorized_payer: buyForm.authorized_payer || null,
      })).unwrap();
      showToast('✅ تم تسجيل المشتريات');
      setBuyOpen(false); setBuyForm({ ...emptyForm(), authorized_payer: '' });
      dispatch(fetchStorePurchasesData());
    } catch { showToast('❌ فشل في تسجيل المشتريات'); }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`حذف هذا السجل؟`)) return;
    try {
      await dispatch(deletePurchase(id)).unwrap();
      showToast('✅ تم الحذف');
      dispatch(fetchStorePurchasesData());
    } catch { showToast('❌ فشل الحذف'); }
  };

  // suppliers
  const openAddSup = () => { setEditSup(null); setSupForm({ name:'',phone:'',email:'',address:'',notes:'' }); setSupOpen(true); };
  const openEditSup = (s) => { setEditSup(s); setSupForm({ name:s.name, phone:s.phone||'', email:s.email||'', address:s.address||'', notes:s.notes||'' }); setSupOpen(true); };
  const handleSaveSup = async () => {
    if (!supForm.name.trim()) { showToast('❌ يرجى إدخال الاسم'); return; }
    try {
      if (editSup) { await dispatch(updateSupplier({ id: editSup.id, ...supForm })).unwrap(); showToast('✅ تم التحديث'); }
      else          { await dispatch(createSupplier(supForm)).unwrap(); showToast('✅ تم الإضافة'); }
      setSupOpen(false); dispatch(fetchStorePurchasesData());
    } catch { showToast('❌ فشل الحفظ'); }
  };
  const handleDelSup = async (s) => {
    if (!window.confirm(`حذف "${s.name}"؟`)) return;
    try { await dispatch(deleteSupplier(s.id)).unwrap(); showToast('✅ تم الحذف'); dispatch(fetchStorePurchasesData()); }
    catch { showToast('❌ فشل الحذف'); }
  };

  // inventory
  const handleUse = async () => {
    if (!useData.quantity || parseInt(useData.quantity) <= 0) { showToast('❌ كمية غير صحيحة'); return; }
    if (parseInt(useData.quantity) > selInvItem.current_stock) { showToast(`❌ المتاح: ${selInvItem.current_stock}`); return; }
    try {
      await dispatch(recordInventoryUsage({ id: selInvItem.id, quantity: parseInt(useData.quantity), notes: useData.notes })).unwrap();
      showToast('✅ تم التسجيل'); setUseOpen(false); setSelInvItem(null); setUseData({ quantity:'', notes:'' });
      dispatch(fetchStorePurchasesData());
    } catch (e) { showToast(e?.payload?.error || '❌ فشل'); }
  };
  const handleProd = async () => {
    if (!prodData.item_name.trim() || !prodData.quantity) { showToast('❌ يرجى إدخال الاسم والكمية'); return; }
    try {
      await dispatch(addProduction({ ...prodData, quantity: parseInt(prodData.quantity), purchase_price: prodData.purchase_price ? parseFloat(prodData.purchase_price) : null, sale_price: prodData.sale_price ? parseFloat(prodData.sale_price) : null })).unwrap();
      showToast('✅ تم إضافة الإنتاج'); setProdOpen(false); setProdData({ item_name:'', quantity:'', purchase_price:'', sale_price:'', notes:'' });
      dispatch(fetchStorePurchasesData());
    } catch (e) { showToast(e?.payload?.error || '❌ فشل'); }
  };

  const handleAdd = async () => {
    const qty = parseInt(addQty);
    if (!qty || qty <= 0) { showToast('❌ كمية غير صحيحة'); return; }
    try {
      await dispatch(adjustInventoryStock({
        id: selAddItem.id,
        quantity: selAddItem.current_stock + qty,
        notes: `إضافة يدوية: +${qty}`,
      })).unwrap();
      showToast('✅ تم تحديث الكمية');
      setAddOpen(false); setSelAddItem(null); setAddQty('');
      dispatch(fetchStorePurchasesData());
    } catch (e) { showToast(e?.payload?.error || '❌ فشل'); }
  };

  const handleSaveCat = async () => {
    if (!catForm.name.trim()) { showToast('❌ يرجى إدخال اسم النوع'); return; }
    setCatSaving(true);
    try {
      await dispatch(createItemCategory({ name: catForm.name, description: catForm.description })).unwrap();
      if (catForm.quantity && parseInt(catForm.quantity) > 0) {
        await dispatch(addProduction({
          item_name: catForm.name,
          quantity: parseInt(catForm.quantity),
        })).unwrap();
      }
      showToast('✅ تم إضافة النوع');
      dispatch(fetchItemCategories());
      dispatch(fetchStorePurchasesData());
      setCatOpen(false);
      setCatForm({ name: '', description: '', quantity: '' });
    } catch (e) {
      const msg = e?.name?.[0] || e?.detail || 'فشل في الحفظ';
      showToast(`❌ ${msg}`);
    } finally {
      setCatSaving(false);
    }
  };

  // print
  const handlePrint = (rows, title) => {
    if (!rows.length) { showToast('❌ لا توجد بيانات'); return; }
    const t = total(rows);
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8"><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px}h1{text-align:center;color:#4d4cac}
    table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:8px;text-align:right}
    th{background:#4d4cac;color:#fff}tr:nth-child(even){background:#f9f9f9}
    .sum{margin-top:20px;padding:12px;background:#f5f5f5;border-radius:5px;font-weight:bold}</style></head><body>
    <h1>${title}</h1>
    <table><thead><tr><th>التاريخ</th><th>النوع</th><th>الكمية</th><th>السعر</th><th>الإجمالي</th></tr></thead><tbody>
    ${rows.map(r=>`<tr><td>${fmtDate(r.purchase_date)}</td><td>${r.item_name||r.category||'-'}</td><td>${r.quantity}</td><td>${Number(r.unit_price).toFixed(2)} ₪</td><td>${Number(r.total_amount||r.quantity*r.unit_price).toFixed(2)} ₪</td></tr>`).join('')}
    </tbody></table><div class="sum">الإجمالي: ${t.toFixed(2)} ₪</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 250);
  };

  // ── summary values ─────────────────────────────────────────────────────────
  const income  = Number(summary?.total_income  || 0);
  const outcome = Number(summary?.total_outcome || 0);
  const profit  = Number(summary?.net_profit    || 0);
  const invCount = (inventory || []).length;

  // ── shared date filter bar ─────────────────────────────────────────────────
  const DateFilterBar = ({ filter, setFilter, from, setFrom, to, setTo }) => (
    <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap" alignItems="center" gap={1}>
      {['all','today','week','custom'].map(f => {
        const active = filter === f;
        return (
          <Chip key={f} size="small"
            label={f==='all'?'الكل':f==='today'?'اليوم':f==='week'?'أسبوع':'مخصص'}
            onClick={()=>setFilter(f)}
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              borderRadius: 2,
              cursor: 'pointer',
              bgcolor: active ? '#4338CA' : '#EEF2FF',
              color: active ? '#fff' : '#4338CA',
              border: active ? 'none' : '1px solid #E5E7EB',
              '&:hover': { bgcolor: active ? '#3730A3' : '#E0E7FF' },
            }}
          />
        );
      })}
      {filter==='custom' && <>
        <TextField size="small" type="date" label="من" value={from} onChange={e=>setFrom(e.target.value)} InputLabelProps={{shrink:true}} sx={{minWidth:140}} />
        <TextField size="small" type="date" label="إلى" value={to}   onChange={e=>setTo(e.target.value)}   InputLabelProps={{shrink:true}} sx={{minWidth:140}} />
      </>}
    </Stack>
  );

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconShoppingCart size={30} color="#fff" />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: FONT }}>
                مبيعات المدرسة والمستودع
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}>
                إدارة المبيعات والمشتريات والمخزون والموردين
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<IconCategory size={18} />}
            onClick={() => { setCatForm({ name: '', description: '', quantity: '' }); setCatOpen(true); }}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              fontFamily: FONT,
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة نوع جديد
          </Button>
        </MotionPaper>

        {/* ── Summary cards ──────────────────────────────────────────────── */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { icon: <IconTrendingUp size={22} color="#16A34A" />, label: 'إجمالي الدخل', value: `${income.toFixed(2)} ₪`, hint: 'مبيعات للطلاب', bg: '#DCFCE7', color: '#16A34A' },
              { icon: <IconTrendingDown size={22} color="#DC2626" />, label: 'إجمالي المصروف', value: `${outcome.toFixed(2)} ₪`, hint: 'مشتريات', bg: '#FEE2E2', color: '#DC2626' },
              { icon: <IconCash size={22} color={profit >= 0 ? '#16A34A' : '#D97706'} />, label: 'صافي الربح', value: `${profit.toFixed(2)} ₪`, hint: 'دخل − مصروف', bg: profit >= 0 ? '#DCFCE7' : '#FEF3C7', color: profit >= 0 ? '#16A34A' : '#D97706' },
              { icon: <IconBox size={22} color="#0284C7" />, label: 'أصناف المخزون', value: invCount, hint: 'صنف مسجل', bg: '#E0F2FE', color: '#0284C7' },
            ].map((c, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Box sx={{ bgcolor: c.bg, borderRadius: 2, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {c.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily: FONT, fontWeight: 700, color: '#6B7280' }}>{c.label}</Typography>
                  </Stack>
                  <Typography variant="h5" sx={{ fontFamily: FONT, fontWeight: 800, color: c.color }}>{c.value}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: FONT, color: '#6B7280' }}>{c.hint}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderTop: '1px solid #E5E7EB',
              pt: 1,
              '& .MuiTab-root': { fontFamily: FONT, fontWeight: 700, textTransform: 'none', color: '#6B7280' },
              '& .Mui-selected': { color: '#4338CA !important' },
              '& .MuiTabs-indicator': { bgcolor: '#4338CA' },
            }}
          >
            <Tab label="المبيعات" icon={<IconShoppingCart size={18} />} iconPosition="start" />
            <Tab label="المشتريات" icon={<IconTruck size={18} />} iconPosition="start" />
            <Tab label="المخزون" icon={<IconBox size={18} />} iconPosition="start" />
          </Tabs>
        </MotionPaper>

        {/* ── TAB 0: المبيعات ───────────────────────────────────────────── */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
              <DateFilterBar filter={dateFilter} setFilter={setDateFilter} from={dateFrom} setFrom={setDateFrom} to={dateTo} setTo={setDateTo} />
              <Stack useFlexGap direction="row" spacing={1.5}>
                <Button variant="outlined" size="small" startIcon={<IconPrinter size={16}/>} onClick={() => handlePrint(filteredSales, 'تقرير المبيعات')}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, borderColor: '#4338CA', color: '#4338CA', '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' } }}>طباعة</Button>
                <Button variant="contained" startIcon={<IconPlus size={16}/>} onClick={() => { setSaleForm(emptyForm()); setSaleOpen(true); }}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>تسجيل بيع</Button>
              </Stack>
            </Stack>
          </Box>

          {loading ? <Box sx={{ textAlign:'center', p:4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
          : filteredSales.length === 0 ? <Alert severity="info" sx={{ borderRadius: 2, fontFamily: FONT }}>لا توجد مبيعات</Alert>
          : <>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', boxShadow: '0 2px 12px rgba(67,56,202,0.07)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontFamily: FONT, fontWeight: 700, bgcolor: '#F1F5F9', color: '#1E1B4B', borderBottom: '1px solid #E5E7EB' } }}>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>النوع</TableCell>
                    <TableCell align="right">الكمية</TableCell>
                    <TableCell align="right">السعر</TableCell>
                    <TableCell align="right">الإجمالي</TableCell>
                    <TableCell align="center">حذف</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSales.map(p => (
                    <TableRow key={p.id} hover sx={{ '& td': { fontFamily: FONT, fontSize: '0.875rem', color: '#1E1B4B', borderColor: '#E5E7EB' }, '&:hover': { bgcolor: '#F8F9FF' } }}>
                      <TableCell>{fmtDate(p.purchase_date)}</TableCell>
                      <TableCell><Chip label={p.item_name || p.category || '—'} size="small" variant="outlined" sx={{ fontFamily: FONT, fontWeight: 600, borderColor: '#E5E7EB', color: '#1E1B4B' }} /></TableCell>
                      <TableCell align="right">{p.quantity}</TableCell>
                      <TableCell align="right">{Number(p.unit_price).toFixed(2)} ₪</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#16A34A' }}>
                        {Number(p.total_amount || p.quantity * p.unit_price).toFixed(2)} ₪
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="حذف">
                          <IconButton size="small" onClick={() => handleDelete(p.id)}
                            sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}><IconTrash size={16}/></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 2, mt: 1.5, borderRadius: 3, bgcolor: '#DCFCE7', display: 'flex', justifyContent: 'flex-end' }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, color: '#16A34A' }}>الإجمالي: {total(filteredSales).toFixed(2)} ₪</Typography>
            </Box>
          </>}
        </TabPanel>

        {/* ── TAB 1: المشتريات ──────────────────────────────────────────── */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
              <DateFilterBar filter={spDateFilter} setFilter={setSpDateFilter} from={spDateFrom} setFrom={setSpDateFrom} to={spDateTo} setTo={setSpDateTo} />
              <Stack useFlexGap direction="row" spacing={1.5}>
                <Button variant="outlined" size="small" startIcon={<IconPrinter size={16}/>} onClick={() => handlePrint(filteredBuys, 'تقرير المشتريات')}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, borderColor: '#4338CA', color: '#4338CA', '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' } }}>طباعة</Button>
                <Button variant="contained" color="secondary" startIcon={<IconPlus size={16}/>} onClick={() => { setBuyForm({ ...emptyForm(), authorized_payer: '' }); setBuyOpen(true); }}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>تسجيل مشتريات</Button>
              </Stack>
            </Stack>
          </Box>

          {loading ? <Box sx={{ textAlign:'center', p:4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
          : filteredBuys.length === 0 ? <Alert severity="info" sx={{ borderRadius: 2, fontFamily: FONT }}>لا توجد مشتريات</Alert>
          : <>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', boxShadow: '0 2px 12px rgba(67,56,202,0.07)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontFamily: FONT, fontWeight: 700, bgcolor: '#F1F5F9', color: '#1E1B4B', borderBottom: '1px solid #E5E7EB' } }}>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>النوع</TableCell>
                    <TableCell>المورد</TableCell>
                    <TableCell align="right">الكمية</TableCell>
                    <TableCell align="right">السعر</TableCell>
                    <TableCell align="right">الإجمالي</TableCell>
                    <TableCell align="center">حذف</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBuys.map(p => (
                    <TableRow key={p.id} hover sx={{ '& td': { fontFamily: FONT, fontSize: '0.875rem', color: '#1E1B4B', borderColor: '#E5E7EB' }, '&:hover': { bgcolor: '#F8F9FF' } }}>
                      <TableCell>{fmtDate(p.purchase_date)}</TableCell>
                      <TableCell><Chip label={p.item_name || p.category || '—'} size="small" variant="outlined" sx={{ fontFamily: FONT, fontWeight: 600, borderColor: '#FDE68A', color: '#D97706' }} /></TableCell>
                      <TableCell>
                        {p.authorized_payer_name
                          ? <Chip label={p.authorized_payer_name} size="small" sx={{ fontFamily: FONT, fontWeight: 600, bgcolor: '#EEF2FF', color: '#4338CA' }} />
                          : <Typography variant="body2" sx={{ fontFamily: FONT, color: '#9CA3AF' }}>—</Typography>
                        }
                      </TableCell>
                      <TableCell align="right">{p.quantity}</TableCell>
                      <TableCell align="right">{Number(p.unit_price).toFixed(2)} ₪</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#DC2626' }}>
                        {Number(p.total_amount || p.quantity * p.unit_price).toFixed(2)} ₪
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="حذف">
                          <IconButton size="small" onClick={() => handleDelete(p.id)}
                            sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}><IconTrash size={16}/></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 2, mt: 1.5, borderRadius: 3, bgcolor: '#FEE2E2', display: 'flex', justifyContent: 'flex-end' }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, color: '#DC2626' }}>الإجمالي: {total(filteredBuys).toFixed(2)} ₪</Typography>
            </Box>
          </>}
        </TabPanel>

        {/* ── TAB 2: المخزون ────────────────────────────────────────────── */}
        <TabPanel value={activeTab} index={2}>
          {loading ? <Box sx={{ textAlign:'center', p:4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
          : !(inventory||[]).length ? <Alert severity="info" sx={{ borderRadius: 2, fontFamily: FONT }}>لا يوجد مخزون</Alert>
          : <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', boxShadow: '0 2px 12px rgba(67,56,202,0.07)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontFamily: FONT, fontWeight: 700, bgcolor: '#F1F5F9', color: '#1E1B4B', borderBottom: '1px solid #E5E7EB' } }}>
                  <TableCell>الصنف</TableCell>
                  <TableCell align="right">المخزون</TableCell>
                  <TableCell align="right">سعر الشراء</TableCell>
                  <TableCell align="right">سعر البيع</TableCell>
                  <TableCell align="right">ربح/وحدة</TableCell>
                  <TableCell align="center">تعديل الكمية</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(inventory||[]).map(item => {
                  const stock = Number(item.current_stock||0);
                  const profit = Number(item.profit_per_unit||0);
                  const stockStyle = stock === 0
                    ? { bgcolor: '#FEE2E2', color: '#DC2626' }
                    : stock < 5
                    ? { bgcolor: '#FEF3C7', color: '#D97706' }
                    : { bgcolor: '#DCFCE7', color: '#16A34A' };
                  return (
                    <TableRow key={item.id} hover sx={{ '& td': { fontFamily: FONT, fontSize: '0.875rem', color: '#1E1B4B', borderColor: '#E5E7EB' }, '&:hover': { bgcolor: '#F8F9FF' } }}>
                      <TableCell sx={{ fontWeight: 700 }}>{item.item_name}</TableCell>
                      <TableCell align="right">
                        <Chip label={stock} size="small" sx={{ fontFamily: FONT, fontWeight: 700, ...stockStyle }} />
                      </TableCell>
                      <TableCell align="right">{Number(item.purchase_price||0).toFixed(2)} ₪</TableCell>
                      <TableCell align="right">{Number(item.sale_price||0).toFixed(2)} ₪</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: FONT, fontWeight: 700, color: profit >= 0 ? '#16A34A' : '#DC2626' }}>
                          {profit.toFixed(2)} ₪
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack useFlexGap direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="زيادة الكمية">
                            <IconButton size="small"
                              onClick={() => { setSelAddItem(item); setAddQty(''); setAddOpen(true); }}
                              sx={{ color: '#16A34A', '&:hover': { bgcolor: '#DCFCE7' } }}>
                              <IconPlus size={16}/>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="تسجيل استخدام / تخفيض">
                            <IconButton size="small" disabled={stock===0}
                              onClick={() => { setSelInvItem(item); setUseData({quantity:'',notes:''}); setUseOpen(true); }}
                              sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}>
                              <IconMinus size={16}/>
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>}
        </TabPanel>

        {/* ════ DIALOGS ════════════════════════════════════════════════════ */}

        {/* Sale */}
        <TransactionDialog open={saleOpen} onClose={() => setSaleOpen(false)}
          title="تسجيل بيع" form={saleForm} setForm={setSaleForm} onSave={handleSaveSale}
          saving={saving} itemCategories={itemCategories} color="primary" />

        {/* Buy */}
        <TransactionDialog open={buyOpen} onClose={() => setBuyOpen(false)}
          title="تسجيل مشتريات" form={buyForm} setForm={setBuyForm} onSave={handleSaveBuy}
          saving={saving} itemCategories={itemCategories} color="secondary" suppliers={authorizedPayers} />

        {/* Supplier */}
        <Dialog open={supOpen} onClose={() => setSupOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT, direction: 'rtl' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, color: '#4338CA', fontSize: '1.15rem' }}>{editSup ? 'تعديل المورد' : 'إضافة مورد'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt:1 }}>
              <TextField fullWidth label="الاسم" value={supForm.name} onChange={e=>setSupForm({...supForm,name:e.target.value})} required />
              <TextField fullWidth label="الهاتف" value={supForm.phone} onChange={e=>setSupForm({...supForm,phone:e.target.value})} />
              <TextField fullWidth label="البريد" type="email" value={supForm.email} onChange={e=>setSupForm({...supForm,email:e.target.value})} />
              <TextField fullWidth label="العنوان" multiline rows={2} value={supForm.address} onChange={e=>setSupForm({...supForm,address:e.target.value})} />
              <TextField fullWidth label="ملاحظات" multiline rows={2} value={supForm.notes} onChange={e=>setSupForm({...supForm,notes:e.target.value})} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setSupOpen(false)} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, color: '#6B7280', borderColor: '#E5E7EB' }}>إلغاء</Button>
            <Button variant="contained" onClick={handleSaveSup} disabled={saving} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>{saving?'...':'حفظ'}</Button>
          </DialogActions>
        </Dialog>

        {/* Add stock */}
        <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT, direction: 'rtl' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, color: '#16A34A', fontSize: '1.15rem' }}>زيادة الكمية — {selAddItem?.item_name}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ borderRadius: 2, fontFamily: FONT }}>الكمية الحالية: {selAddItem?.current_stock}</Alert>
              <TextField fullWidth label="الكمية المضافة" type="number" value={addQty}
                onChange={e => setAddQty(e.target.value)} inputProps={{ min: 1 }} required />
              {addQty && parseInt(addQty) > 0 && (
                <Alert severity="info" sx={{ borderRadius: 2, fontFamily: FONT }}>
                  الكمية بعد الإضافة: {(selAddItem?.current_stock || 0) + parseInt(addQty)}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setAddOpen(false)} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, color: '#6B7280', borderColor: '#E5E7EB' }}>إلغاء</Button>
            <Button variant="contained" onClick={handleAdd} disabled={saving} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#16A34A', boxShadow: 'none', '&:hover': { bgcolor: '#15803D', boxShadow: 'none' } }}>{saving ? '...' : 'إضافة'}</Button>
          </DialogActions>
        </Dialog>

        {/* Use inventory */}
        <Dialog open={useOpen} onClose={() => setUseOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT, direction: 'rtl' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, color: '#4338CA', fontSize: '1.15rem' }}>استخدام — {selInvItem?.item_name}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt:1 }}>
              <Alert severity="info" sx={{ borderRadius: 2, fontFamily: FONT }}>المتاح: {selInvItem?.current_stock}</Alert>
              <TextField fullWidth label="الكمية" type="number" value={useData.quantity}
                onChange={e=>setUseData({...useData,quantity:e.target.value})} inputProps={{min:1,max:selInvItem?.current_stock}} required />
              <TextField fullWidth label="ملاحظات" multiline rows={2} value={useData.notes} onChange={e=>setUseData({...useData,notes:e.target.value})} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setUseOpen(false)} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, color: '#6B7280', borderColor: '#E5E7EB' }}>إلغاء</Button>
            <Button variant="contained" onClick={handleUse} disabled={saving} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>{saving?'...':'تسجيل'}</Button>
          </DialogActions>
        </Dialog>

        {/* Production */}
        <Dialog open={prodOpen} onClose={() => setProdOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT, direction: 'rtl' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, color: '#4338CA', fontSize: '1.15rem' }}>إضافة إنتاج للمخزون</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt:1 }}>
              <TextField fullWidth label="اسم الصنف" value={prodData.item_name} onChange={e=>setProdData({...prodData,item_name:e.target.value})} required />
              <TextField fullWidth label="الكمية" type="number" value={prodData.quantity} onChange={e=>setProdData({...prodData,quantity:e.target.value})} required />
              <TextField fullWidth label="سعر التكلفة (اختياري)" type="number" value={prodData.purchase_price} onChange={e=>setProdData({...prodData,purchase_price:e.target.value})} />
              <TextField fullWidth label="سعر البيع (اختياري)" type="number" value={prodData.sale_price} onChange={e=>setProdData({...prodData,sale_price:e.target.value})} />
              <TextField fullWidth label="ملاحظات" multiline rows={2} value={prodData.notes} onChange={e=>setProdData({...prodData,notes:e.target.value})} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setProdOpen(false)} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, color: '#6B7280', borderColor: '#E5E7EB' }}>إلغاء</Button>
            <Button variant="contained" onClick={handleProd} disabled={saving} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>{saving?'...':'إضافة'}</Button>
          </DialogActions>
        </Dialog>

        {/* Add Category */}
        <Dialog open={catOpen} onClose={() => setCatOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT, direction: 'rtl' } }}>
          <DialogTitle sx={{
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff', fontWeight: 800, fontFamily: FONT, fontSize: '1.15rem',
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <IconCategory size={20} /> إضافة نوع جديد
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField fullWidth required label="اسم النوع" value={catForm.name}
                onChange={e => setCatForm({ ...catForm, name: e.target.value })}
                placeholder="مثال: أقلام، ألوان، فرش، دفاتر..."
                helperText="سيظهر هذا الاسم كخيار في قوائم البيع والشراء"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontFamily: FONT, '&:hover fieldset': { borderColor: '#4338CA' }, '&.Mui-focused fieldset': { borderColor: '#4338CA' } }, '& .MuiInputLabel-root': { fontFamily: FONT } }} />
              <TextField fullWidth label="وصف (اختياري)" multiline rows={2} value={catForm.description}
                onChange={e => setCatForm({ ...catForm, description: e.target.value })}
                placeholder="وصف مختصر..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontFamily: FONT, '&:hover fieldset': { borderColor: '#4338CA' }, '&.Mui-focused fieldset': { borderColor: '#4338CA' } }, '& .MuiInputLabel-root': { fontFamily: FONT } }} />
              <TextField fullWidth label="الكمية الابتدائية (اختياري)" type="number" inputProps={{ min: 0 }}
                value={catForm.quantity}
                onChange={e => setCatForm({ ...catForm, quantity: e.target.value })}
                helperText="إذا أردت إضافة كمية أولية للمخزون فور إنشاء النوع"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontFamily: FONT, '&:hover fieldset': { borderColor: '#4338CA' }, '&.Mui-focused fieldset': { borderColor: '#4338CA' } }, '& .MuiInputLabel-root': { fontFamily: FONT } }} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setCatOpen(false)} variant="outlined"
              sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, color: '#6B7280', borderColor: '#E5E7EB', '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' } }}>إلغاء</Button>
            <Button variant="contained" onClick={handleSaveCat} disabled={catSaving || !catForm.name.trim()}
              sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#4338CA', boxShadow: 'none', px: 3, '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>
              {catSaving ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default StorePurchasesPage;
